import { orderBy, uniqBy } from 'lodash';
import React, { memo, ReactNode, useCallback, useMemo } from 'react';
import ProspectTag from '../../atoms/prospect-tag';
import './prospect-tags-row.scss';

export type ProspectTagsRowProps = {
  /**
   * No of tags to display in a row. Other tags will be grouped together.
   */
  maxTags?: number;
  tags: any[];
  renderAddTag?: () => ReactNode;
  size?: number;
  isColumnMode?: boolean;
  tagLength?: number;
  isPosition?: boolean;
  emptyPlaceString?: string;
  automaticallyShowTooltip?: boolean;
  closeOnScroll?: boolean;
};

/**
 * Renders a Row of prospect tags.
 * Max no of tags in a row can be limited.
 * All the other tags will be grouped together.
 */
const ProspectTagsRow: React.FC<ProspectTagsRowProps> = ({
  tags,
  maxTags = tags?.length > 2 ? 1 : 2,
  renderAddTag,
  size,
  isColumnMode = false,
  tagLength = 10,
  isPosition = false,
  emptyPlaceString = '-',
  automaticallyShowTooltip = false,
  closeOnScroll = false,
}) => {
  const sortedTags = useMemo(
    () => orderBy(uniqBy(tags ?? [], 'id'), 'id', 'desc'),
    [tags],
  );

  let groupedTagNames: React.ReactNode = '';

  const [tagsToDisplay, tagsToGroup] = [
    sortedTags.slice(0, maxTags),
    sortedTags.slice(maxTags),
  ];

  groupedTagNames = useMemo(
    () => tagsToGroup.map(({ name, id }) => <div key={id}>{name}</div>),
    [tagsToGroup],
  );

  const renderTags = useCallback(() => {
    const row = tagsToDisplay.map(({ name, id }) => (
      <div key={id}>
        <ProspectTag
          name={name}
          tooltipText={name}
          isPopoverVisible={false}
          tagLength={tagLength}
          automaticallyShowTooltip={automaticallyShowTooltip}
        />
      </div>
    ));

    if (tagsToGroup.length) {
      row.push(
        <div key="tags-overflow-more">
          <ProspectTag
            name={`+${tagsToGroup.length}`}
            tooltipText={groupedTagNames}
            isPopoverVisible={true}
            tagLength={tagLength}
            position={isPosition ? 'bottom' : 'top'}
            automaticallyShowTooltip={automaticallyShowTooltip}
            closeOnScroll={closeOnScroll}
          />
        </div>,
      );
    }

    return row;
  }, [groupedTagNames, tagsToDisplay, tagsToGroup.length, automaticallyShowTooltip]);

  return (
    <div
      className={`${isColumnMode ? 'prospect-tag-column' : 'prospect-tag-row'}`}
    >
      {tagsToDisplay.length ? renderTags() : emptyPlaceString}
      {renderAddTag && renderAddTag()}
    </div>
  );
};

export default memo(ProspectTagsRow);
